/** Google Analytics 4 测量 ID，形如 G-XXXXXXXXXX */
export const GA_MEASUREMENT_ID = import.meta.env.VITE_GA_MEASUREMENT_ID ?? ''

/** 百度统计 hm.js 站点 ID（仅数字） */
export const BAIDU_HM_ID = import.meta.env.VITE_BAIDU_HM_ID ?? ''

const ANALYTICS_IN_DEV = import.meta.env.VITE_ANALYTICS_IN_DEV === 'true'

declare global {
  interface Window {
    dataLayer?: unknown[]
    gtag?: (...args: unknown[]) => void
    _hmt?: unknown[]
  }
}

function shouldRunAnalytics(): boolean {
  if (!GA_MEASUREMENT_ID && !BAIDU_HM_ID) return false
  if (import.meta.env.DEV && !ANALYTICS_IN_DEV) return false
  return true
}

function loadScript(src: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const el = document.createElement('script')
    el.async = true
    el.src = src
    el.onload = () => resolve()
    el.onerror = () => reject(new Error(`Failed to load: ${src}`))
    document.head.appendChild(el)
  })
}

async function initGoogleAnalytics(): Promise<void> {
  if (!GA_MEASUREMENT_ID) return

  window.dataLayer = window.dataLayer ?? []
  window.gtag = function gtag(...args: unknown[]) {
    window.dataLayer!.push(args)
  }
  window.gtag('js', new Date())
  await loadScript(`https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`)
  window.gtag('config', GA_MEASUREMENT_ID, { send_page_view: false })
}

async function initBaiduAnalytics(): Promise<void> {
  if (!BAIDU_HM_ID) return
  window._hmt = window._hmt ?? []
  await loadScript(`https://hm.baidu.com/hm.js?${BAIDU_HM_ID}`)
}

/** 当前页路径（含 hash，适配 SPA） */
export function getAnalyticsPagePath(): string {
  return `${window.location.pathname}${window.location.hash || '#/'}`
}

/** 应用启动时加载统计脚本（仅当配置了 ID） */
export async function initAnalytics(): Promise<void> {
  if (!shouldRunAnalytics()) return
  try {
    await Promise.all([initGoogleAnalytics(), initBaiduAnalytics()])
    trackPageView()
  } catch (err) {
    console.warn('[analytics] init failed', err)
  }
}

/** 路由切换或首屏加载后上报 PV */
export function trackPageView(pagePath?: string): void {
  if (!shouldRunAnalytics()) return

  const path = pagePath ?? getAnalyticsPagePath()
  const title = document.title

  if (window.gtag && GA_MEASUREMENT_ID) {
    window.gtag('event', 'page_view', {
      page_path: path,
      page_title: title,
    })
  }

  if (window._hmt && BAIDU_HM_ID) {
    window._hmt.push(['_trackPageview', path])
  }
}

export interface AnalyticsEventParams {
  mode?: string
  score?: number
  accuracy?: number
  [key: string]: string | number | undefined
}

/** 自定义事件（如完成一局训练） */
export function trackEvent(action: string, params: AnalyticsEventParams = {}): void {
  if (!shouldRunAnalytics()) return

  const label = params.mode ?? ''

  if (window.gtag && GA_MEASUREMENT_ID) {
    window.gtag('event', action, params)
  }

  if (window._hmt && BAIDU_HM_ID) {
    window._hmt.push([
      '_trackEvent',
      'FpsTrain',
      action,
      label,
      params.score ?? 0,
    ])
  }
}

export function isAnalyticsConfigured(): boolean {
  return Boolean(GA_MEASUREMENT_ID || BAIDU_HM_ID)
}
